import { type LucideIcon } from "lucide-react";

import { Button as BaseButton } from "@/shared/components/primitives/button";
import type { ButtonProps as BaseButtonProps } from "@/shared/components/primitives/button";
import { Spinner } from "@/shared/components/primitives/spinner";
import { cn } from "@/shared/lib/utils";

interface ColocaButtonProps extends BaseButtonProps {
  size?: "default" | "xs" | "sm" | "lg";
  /**
   * Use to reinforce the action with a visual cue — e.g. `<Save>` on a submit button or `<Download>` on an
   * export. Prefer a leading icon when the icon describes _what the button does_.
   */
  icon?: LucideIcon;
  /**
   * Use to indicate the outcome or direction of the action — e.g. `<ArrowRight>` for navigation or
   * `<ExternalLink>` when the action opens a new tab. Prefer a trailing icon when the icon describes _what
   * happens after_ clicking.
   */
  iconTrailing?: LucideIcon;
  /**
   * Set to `true` while an async operation is in progress. Disables the button so users cannot submit twice
   * while waiting for a response.
   */
  loading?: boolean;
}

/**
 * Standard labeled button for the Coloca design system.
 *
 * Use `ColocaButton` whenever the action needs a visible text label. For icon-only actions use
 * `ColocaIconButton` instead.
 */
export function ColocaButton({
  icon: Icon,
  iconTrailing: IconTrailing,
  loading = false,
  children,
  disabled,
  className,
  ...props
}: ColocaButtonProps) {
  return (
    <BaseButton disabled={disabled || loading} className={cn(className, "relative")} {...props}>
      {loading ? <Spinner data-icon="inline-start" className="animate-spin" /> : null}
      {!loading && Icon ? <Icon data-icon="inline-start" /> : null}
      {children}
      {!loading && IconTrailing ? <IconTrailing data-icon="inline-end" /> : null}
    </BaseButton>
  );
}
